import React, { FC, Fragment } from "react";
import { FormControl, Grid, RadioGroup, makeStyles, createStyles } from "@material-ui/core";
import { FormControlLabel } from "../core/FormControlLabel";
import { Checkbox } from "../core/Checkbox";
import { useStyles } from "../../useStyles";
import { useFormModel } from "../core/FormModelHelpes";
import { ChangeNames, ChangeTypes } from "../../clients/FirebaseClient/types";
import { Radio } from "../core/Radio";
import { LabledTextField } from "../core/LabledTextField";

const convertValue = (
    value: ChangeTypes[ChangeNames.NEW_CONNECTION_MULTILINE_VARIANTS]["value"],
): string => {
    if (value.waterPipeDiameters.value && value.waterPipeDiameters.value.da63) {
        return "da63";
    } else if (value.waterPipeDiameters.value && value.waterPipeDiameters.value.lessOrEqualDa50) {
        return "lessOrEqualDa50";
    }
    return "";
};

const useFormControlStyle = makeStyles(() =>
    createStyles({
        container: {
            display: "flex",
            flexDirection: "row",
        },
        formControlLabelCheckboxStyle: {
            width: 300,
        },
        checkboxStyle: {
            marginRight: 8,
        },
        radioGroupElement: {
            marginRight: 38,
        },
    }),
);

export const NewConnectionMultilineVariantsForm: FC = () => {
    const classes = useStyles();

    const { values, setValues, submitChange } = useFormModel<
        ChangeNames.NEW_CONNECTION_MULTILINE_VARIANTS
    >();

    const selection = convertValue(values.newConnectionMultilineVariants.value);
    const formControlClasses = useFormControlStyle();

    return (
        <Fragment>
            <FormControl
                component="fieldset"
                className={classes.container}
                style={{ display: "flex" }}
            >
                <h4 className={classes.h4} style={{ marginBottom: 0, marginTop: 40 }}>
                    Durchzuführende Leitungen
                </h4>
                <Grid container spacing={7}>
                    <Grid item>
                        <FormControlLabel
                            isCheckBox={true}
                            className={formControlClasses.formControlLabelCheckboxStyle}
                            control={
                                <Checkbox
                                    className={formControlClasses.checkboxStyle}
                                    value="power"
                                    checked={
                                        values.newConnectionMultilineVariants.value.lines.value
                                            .power
                                    }
                                    onChange={(event, checked) => {
                                        setValues(values => {
                                            values.newConnectionMultilineVariants.value.lines.value.power = checked;
                                        });
                                    }}
                                />
                            }
                            label="Strom"
                        />
                        <FormControlLabel
                            isCheckBox={true}
                            className={formControlClasses.formControlLabelCheckboxStyle}
                            control={
                                <Checkbox
                                    className={formControlClasses.checkboxStyle}
                                    value="gas"
                                    checked={
                                        values.newConnectionMultilineVariants.value.lines.value.gas
                                    }
                                    onChange={(event, checked) => {
                                        setValues(values => {
                                            values.newConnectionMultilineVariants.value.lines.value.gas = checked;
                                        });
                                    }}
                                />
                            }
                            label="Gas/Flüssiggas"
                        />
                        <FormControlLabel
                            isCheckBox={true}
                            className={formControlClasses.formControlLabelCheckboxStyle}
                            control={
                                <Checkbox
                                    className={formControlClasses.checkboxStyle}
                                    value="water"
                                    checked={
                                        values.newConnectionMultilineVariants.value.lines.value
                                            .water
                                    }
                                    onChange={(event, checked) => {
                                        const newValues = setValues(values => {
                                            values.newConnectionMultilineVariants.value.lines.value.water = checked;
                                            if (checked) {
                                                values.newConnectionMultilineVariants.value.waterPipeDiameters.value = {
                                                    da63: false,
                                                    lessOrEqualDa50: false,
                                                };
                                            } else {
                                                values.newConnectionMultilineVariants.value.waterPipeDiameters.value = null;
                                            }
                                        });
                                        submitChange(newValues);
                                    }}
                                />
                            }
                            label="Wasser"
                        />
                        <FormControlLabel
                            isCheckBox={true}
                            className={formControlClasses.formControlLabelCheckboxStyle}
                            control={
                                <Checkbox
                                    className={formControlClasses.checkboxStyle}
                                    value="multimedia"
                                    checked={
                                        values.newConnectionMultilineVariants.value.lines.value
                                            .multimedia
                                    }
                                    onChange={(event, checked) => {
                                        setValues(values => {
                                            values.newConnectionMultilineVariants.value.lines.value.multimedia = checked;
                                        });
                                    }}
                                />
                            }
                            label="Telekommunikation *"
                        />
                        <FormControlLabel
                            isCheckBox={true}
                            className={formControlClasses.formControlLabelCheckboxStyle}
                            control={
                                <Checkbox
                                    className={formControlClasses.checkboxStyle}
                                    value="cistern"
                                    checked={
                                        values.newConnectionMultilineVariants.value.lines.value
                                            .cistern
                                    }
                                    onChange={(event, checked) => {
                                        setValues(values => {
                                            values.newConnectionMultilineVariants.value.lines.value.cistern = checked;
                                        });
                                    }}
                                />
                            }
                            label="Zisterne"
                        />
                        <FormControlLabel
                            isCheckBox={true}
                            className={formControlClasses.formControlLabelCheckboxStyle}
                            control={
                                <Checkbox
                                    className={formControlClasses.checkboxStyle}
                                    value="heatPump"
                                    checked={
                                        values.newConnectionMultilineVariants.value.lines.value
                                            .heatPump
                                    }
                                    onChange={(event, checked) => {
                                        setValues(values => {
                                            values.newConnectionMultilineVariants.value.lines.value.heatPump = checked;
                                        });
                                    }}
                                />
                            }
                            label="Wärmeanlage"
                        />
                        <FormControlLabel
                            isCheckBox={true}
                            style={{ marginBottom: 16 }}
                            className={formControlClasses.formControlLabelCheckboxStyle}
                            control={
                                <Checkbox
                                    className={formControlClasses.checkboxStyle}
                                    value="outdoorLighting"
                                    checked={
                                        values.newConnectionMultilineVariants.value.lines.value
                                            .outdoorLighting
                                    }
                                    onChange={(event, checked) => {
                                        setValues(values => {
                                            values.newConnectionMultilineVariants.value.lines.value.outdoorLighting = checked;
                                        });
                                    }}
                                />
                            }
                            label="Außenbeleuchtung"
                        />
                        <LabledTextField
                            style={{ width: 400 }}
                            margin="normal"
                            value={
                                values.newConnectionMultilineVariants.value.additionalLines.value ||
                                ""
                            }
                            placeholder="Weitere Leitungen"
                            onChange={event => {
                                setValues(values => {
                                    values.newConnectionMultilineVariants.value.additionalLines.value =
                                        event.target.value || "";
                                });
                            }}
                        />
                        <p className={classes.pSmall} style={{ marginBottom: 0 }}>
                            * Durch das Multimedia-Dichtelement können bis zu 7 Leitungen
                            verschiedener Durchmesser geführt werden.
                        </p>
                    </Grid>
                </Grid>

                {values.newConnectionMultilineVariants.value.waterPipeDiameters.value && (
                    <Fragment>
                        <h4 className={classes.h4} style={{ marginBottom: 0, marginTop: 40 }}>
                            Durchzuführende Leitung Wasser
                        </h4>
                        <p className={classes.p} style={{ marginBottom: 0 }}>
                            Geben Sie hier den Außendurchmesser der Wasserleitung an. Diese
                            Information erhalten Sie ggf. von Ihrem Wasserversorger.
                        </p>
                        <FormControl component="fieldset" style={{ display: "flex" }}>
                            <RadioGroup
                                value={selection}
                                onChange={(e, v) => {
                                    setValues(values => {
                                        values.newConnectionMultilineVariants.value.waterPipeDiameters.value = {
                                            da63: v.indexOf("da63") >= 0,
                                            lessOrEqualDa50: v.indexOf("lessOrEqualDa50") >= 0,
                                        };
                                    });
                                }}
                            >
                                <Grid
                                    container
                                    spacing={7}
                                    className={formControlClasses.container}
                                >
                                    <Grid item xs={10} sm={12} md={12}>
                                        <FormControlLabel
                                            className={formControlClasses.radioGroupElement}
                                            value="lessOrEqualDa50"
                                            control={<Radio />}
                                            label="bis einschließlich 50 mm"
                                            error={
                                                values.newConnectionMultilineVariants.error !== null
                                            }
                                        />
                                        <FormControlLabel
                                            className={formControlClasses.radioGroupElement}
                                            value="da63"
                                            control={<Radio />}
                                            label="63 mm"
                                            error={
                                                values.newConnectionMultilineVariants.error !== null
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </RadioGroup>
                        </FormControl>
                    </Fragment>
                )}
            </FormControl>
        </Fragment>
    );
};
